/**
 * Capital Desk API Service  v2
 * ============================
 * REDEX-only deal engine / capital desk endpoints.
 */

import { apiRequest } from './api';

const BASE = '/admin/capital-desk';

// ── Types ────────────────────────────────────────────────────────────

export interface DeskDeal {
  id: string;
  deal_id: string;
  tenant_id: string;
  sponsor_id: string | null;
  sponsor_name?: string;
  sponsor_legal_name?: string;
  sponsor_track_record?: Record<string, unknown>;
  sponsor_contacts?: unknown[];
  title: string;
  summary?: string | null;
  asset_type: string | null;
  strategy?: string | null;
  geography: string | null;
  offering_type?: string | null;
  security_type?: string | null;
  raise_amount_cents: number | null;
  raised_to_date_cents?: number | null;
  min_invest_cents: number | null;
  term_months: number | null;
  target_hold_months?: number | null;
  target_return: number | null;
  target_yield: number | null;
  target_irr_pct?: number | null;
  target_cash_yield_pct?: number | null;
  preferred_return_pct?: number | null;
  distribution_frequency?: string | null;
  accredited_only: boolean;
  status: string;
  source: string;
  source_detail?: Record<string, unknown>;
  asset_detail?: Record<string, unknown>;
  capital_stack?: Record<string, unknown>;
  risk_flags?: string[];
  tags?: string[];
  notes: string | null;
  metadata: Record<string, unknown>;
  total_score?: number | null;
  risk_band?: 'GREEN' | 'YELLOW' | 'RED' | null;
  decision_recommendation?: string | null;
  sponsor_score?: number | null;
  asset_score?: number | null;
  stack_score?: number | null;
  risk_score?: number | null;
  demand_score?: number | null;
  reason_codes?: ReasonCodeEntry[];
  latest_decision?: string | null;
  decision_conditions?: string | null;
  conditions_json?: unknown;
  decision_rationale?: string | null;
  decided_at?: string | null;
  scored_at?: string | null;
  published_at?: string | null;
  published_plan_id?: string | null;
  submitted_at?: string | null;
  created_at: string;
  updated_at: string;
  // Enriched fields from GET /deals/:id
  documents?: DealDocument[];
  features?: DealFeatures | null;
  compliance?: ComplianceState;
  optins?: DealOptin[];
  distribution_plans?: DistributionPlan[];
  deal_notes?: DealNote[];
  evidence?: DealEvidence[];
  doc_count?: number;
}

export interface ReasonCodeEntry {
  code: string;
  weight: number;
  points: number;
  note: string;
  evidence_id?: string | null;
}

export interface ScoreResult {
  score: {
    total_score: number;
    sponsor_score: number;
    asset_score: number;
    stack_score: number;
    risk_score: number;
    demand_score: number;
    reason_codes: ReasonCodeEntry[];
    risk_band: string;
    decision_recommendation: string;
  };
  decision: string;
  risk_band: string;
  pillarScores: Record<string, number>;
  reasonCodes: { code: string; pillar: string; note?: string }[];
}

export interface PipelineStats {
  total_deals: number;
  intake: number;
  under_review: number;
  approved: number;
  listed: number;
  closed: number;
  rejected: number;
  drafts: number;
  avg_score: number | null;
  avg_time_to_decision_days: number | null;
  active_distributions: number;
}

export interface Sponsor {
  id: string;
  name: string;
  legal_name?: string;
  entity_type: string | null;
  contact_name: string | null;
  contact_email: string | null;
  contact_phone?: string | null;
  principal_contacts?: unknown[];
  track_record: Record<string, unknown>;
  bio?: string | null;
  status: string;
}

export interface ComplianceState {
  deal_compliance_state: 'READY' | 'NEEDS_DOCS' | 'RESTRICTED';
  required_disclosures: { doc: string; reason: string }[];
  risk_constraints: { constraint: string; severity: string }[];
  documents_on_file: string[];
}

export interface DealDocument {
  id: string;
  deal_id: string;
  doc_type: string;
  filename: string | null;
  storage_url: string | null;
  sha256_hash: string | null;
  confidence: number | null;
  status: string;
  created_at: string;
}

export interface DealFeatures {
  deal_id: string;
  metrics: Record<string, unknown>;
  validation_flags: { field: string; issue: string; severity: string }[];
}

export interface DealOptin {
  id: string;
  deal_id: string;
  tenant_id: string;
  status: string;
  notes: string | null;
  opted_at: string;
  listed_at: string | null;
}

export interface DistributionPlan {
  id: string;
  deal_id: string;
  mode: string;
  cohort: unknown;
  phases: unknown[];
  access_rules: unknown;
  eligibility: unknown;
  economics: unknown;
  revenue_splits: unknown;
  status: string;
  created_by: string | null;
  audit_info: unknown;
  created_at: string;
}

export interface DealNote {
  id: string;
  deal_id: string;
  note: string;
  tags: string[];
  created_by: string | null;
  created_at: string;
}

export interface DealEvidence {
  id: string;
  evidence_id: string;
  deal_id: string;
  evidence_type: string;
  source: Record<string, unknown>;
  extraction: Record<string, unknown>;
  created_at: string;
}

export interface AuditEntry {
  id: string;
  deal_id: string;
  action: string;
  actor_id: string | null;
  actor_type: string;
  old_value: unknown;
  new_value: unknown;
  note: string | null;
  created_at: string;
}

export interface DemandForecast {
  matchingOperators: number;
  likelyAllocators: { tenant_id: string; name: string }[];
  ticketMismatch: boolean;
  assetClassHot: boolean;
  assetClassCold: boolean;
  networkSaturated: boolean;
  predictedFillDays: number;
  expectedTicketRange: { min: number; max: number } | null;
}

// ── API Calls ────────────────────────────────────────────────────────

export const capitalDeskApi = {
  // Stats
  getStats: () => apiRequest<{ data: PipelineStats }>('GET', `${BASE}/stats`),

  // Deals
  listDeals: (params?: Record<string, string>) => {
    const qs = params ? '?' + new URLSearchParams(params).toString() : '';
    return apiRequest<{ data: { deals: DeskDeal[]; total: number } }>('GET', `${BASE}/deals${qs}`);
  },
  getDeal:    (id: string) => apiRequest<{ data: DeskDeal }>('GET', `${BASE}/deals/${id}`),
  createDeal: (data: Partial<DeskDeal>) => apiRequest<{ data: DeskDeal }>('POST', `${BASE}/deals`, data),
  updateDeal: (id: string, data: Partial<DeskDeal>) => apiRequest<{ data: DeskDeal }>('PUT', `${BASE}/deals/${id}`, data),

  // Scoring
  scoreDeal:  (id: string) => apiRequest<{ data: ScoreResult }>('POST', `${BASE}/deals/${id}/score`),
  getScores:  (id: string) => apiRequest<{ data: unknown[] }>('GET', `${BASE}/deals/${id}/scores`),

  // Decisions (supports conditions_json + deadline)
  decideDeal: (id: string, decision: string, rationale?: string, conditionsJson?: unknown, deadlineDays?: number) =>
    apiRequest<{ data: unknown }>('POST', `${BASE}/deals/${id}/decide`, {
      decision, rationale, conditions_json: conditionsJson, deadline_days: deadlineDays,
    }),
  getDecisions: (id: string) => apiRequest<{ data: unknown[] }>('GET', `${BASE}/deals/${id}/decisions`),

  // Documents
  addDocument:  (id: string, data: Record<string, unknown>) =>
    apiRequest<{ data: DealDocument }>('POST', `${BASE}/deals/${id}/documents`, data),
  getDocuments: (id: string) => apiRequest<{ data: DealDocument[] }>('GET', `${BASE}/deals/${id}/documents`),

  // Features
  updateFeatures: (id: string, metrics: Record<string, unknown>, validationFlags?: unknown[]) =>
    apiRequest<{ data: DealFeatures }>('PUT', `${BASE}/deals/${id}/features`, { metrics, validation_flags: validationFlags }),

  // Distribution
  generatePlan: (id: string, planData?: Record<string, unknown>) =>
    apiRequest<{ data: DistributionPlan }>('POST', `${BASE}/deals/${id}/distribution`, planData || {}),
  getPlans:     (id: string) => apiRequest<{ data: DistributionPlan[] }>('GET', `${BASE}/deals/${id}/distribution`),

  // Publish
  publishDeal: (id: string, planId?: string, publishMode?: string) =>
    apiRequest<{ data: { status: string; published_at: string } }>('POST', `${BASE}/deals/${id}/publish`, {
      plan_id: planId, publish_mode: publishMode,
    }),

  // Compliance
  getCompliance: (id: string) => apiRequest<{ data: ComplianceState }>('GET', `${BASE}/deals/${id}/compliance`),

  // Demand
  getDemand: (id: string) => apiRequest<{ data: DemandForecast }>('GET', `${BASE}/deals/${id}/demand`),

  // Notes
  getNotes: (id: string) => apiRequest<{ data: DealNote[] }>('GET', `${BASE}/deals/${id}/notes`),
  addNote:  (id: string, note: string, tags?: string[]) =>
    apiRequest<{ data: DealNote }>('POST', `${BASE}/deals/${id}/notes`, { note, tags }),

  // Evidence
  getEvidence: (id: string) =>
    apiRequest<{ data: { evidence: DealEvidence[]; summary: { count: number; low_confidence_fields: number } } }>('GET', `${BASE}/deals/${id}/evidence`),
  createEvidence: (id: string, data: Partial<DealEvidence>) =>
    apiRequest<{ data: DealEvidence }>('POST', `${BASE}/deals/${id}/evidence`, data),

  // Extract (placeholder doc field extraction)
  extractDeal: (id: string, docId: string, fields: Record<string, unknown>, confidence?: number) =>
    apiRequest<{ data: DealEvidence }>('POST', `${BASE}/deals/${id}/extract`, {
      doc_id: docId, fields, confidence,
    }),

  // Tenant Opt-ins
  optInTenant:  (id: string, tenantId: string, notes?: string) =>
    apiRequest<{ data: DealOptin }>('POST', `${BASE}/deals/${id}/optins`, { tenant_id: tenantId, notes }),
  updateOptin:  (dealId: string, tenantId: string, status: string) =>
    apiRequest<{ data: DealOptin }>('PUT', `${BASE}/deals/${dealId}/optins/${tenantId}`, { status }),
  getOptins:    (id: string) => apiRequest<{ data: DealOptin[] }>('GET', `${BASE}/deals/${id}/optins`),

  // Sponsors
  listSponsors:  () => apiRequest<{ data: Sponsor[] }>('GET', `${BASE}/sponsors`),
  getSponsor:    (id: string) => apiRequest<{ data: Sponsor }>('GET', `${BASE}/sponsors/${id}`),
  createSponsor: (data: Partial<Sponsor>) => apiRequest<{ data: Sponsor }>('POST', `${BASE}/sponsors`, data),
  updateSponsor: (id: string, data: Partial<Sponsor>) => apiRequest<{ data: Sponsor }>('PUT', `${BASE}/sponsors/${id}`, data),

  // Operators
  listOperators:  () => apiRequest<{ data: unknown[] }>('GET', `${BASE}/operators`),
  upsertOperator: (data: Record<string, unknown>) => apiRequest<{ data: unknown }>('POST', `${BASE}/operators`, data),

  // Audit
  getAudit: (id: string) => apiRequest<{ data: AuditEntry[] }>('GET', `${BASE}/deals/${id}/audit`),

  // Reason codes reference
  getReasonCodes: () =>
    apiRequest<{ data: { code: string; pillar: string; points: number; weight: number; label: string }[] }>('GET', `${BASE}/reason-codes`),
};
